import { Box, TextField } from '@mui/material';
import { parsePhoneNumber } from 'libphonenumber-js';
import { CountrySelector, usePhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form';

import { phoneCountries } from '@/lib/phone';

interface InnerProps {
  value: string;
  onChange: (phone: string) => void;
  onBlur: () => void;
  error?: string;
  label: string;
  disabled: boolean;
}

function PhoneInputInner({ value, onChange, onBlur, error, label, disabled }: InnerProps): JSX.Element {
  const { inputValue, country, setCountry, handlePhoneValueChange, inputRef } = usePhoneInput({
    defaultCountry: 'ar',
    value,
    countries: phoneCountries,
    onChange: ({ phone }) => {
      try {
        const parsed = phone ? parsePhoneNumber(phone) : null;
        onChange(parsed?.nationalNumber ? phone : '');
      } catch {
        onChange('');
      }
    },
  });

  return (
    <TextField
      fullWidth
      label={label}
      value={inputValue}
      onChange={handlePhoneValueChange}
      onBlur={onBlur}
      inputRef={inputRef}
      disabled={disabled}
      error={!!error}
      helperText={error ?? ' '}
      InputProps={{
        startAdornment: (
          <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
            <CountrySelector
              selectedCountry={country.iso2}
              onSelect={({ iso2 }) => setCountry(iso2)}
              disabled={disabled}
              countries={phoneCountries}
            />
          </Box>
        ),
      }}
    />
  );
}

interface PhoneInputFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  label?: string;
  disabled?: boolean;
}

export function PhoneInputField<T extends FieldValues>({
  control,
  name,
  label = 'Teléfono / WhatsApp',
  disabled = false,
}: PhoneInputFieldProps<T>): JSX.Element {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <PhoneInputInner
          value={(field.value as string) ?? ''}
          onChange={field.onChange}
          onBlur={field.onBlur}
          error={fieldState.error?.message}
          label={label}
          disabled={disabled}
        />
      )}
    />
  );
}
